import { Dashboard } from '../dashboard/Dashboard';
import styles from './Home.module.scss';
import { msalInstance } from '@auth/msal-instance.ts';
import { useAuth } from '@hooks/useAuth';
import { useEffect, useState } from 'react';
import { logger } from '@utils/logging-utils';
import type { AccountInfo } from '@azure/msal-browser';
import { Typography } from '@mui/material';

interface AuthState {
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  activeAccount?: AccountInfo | null;
}

export function Home() {
  const { login } = useAuth();
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: true,
    hasError: false
  });

  useEffect(() => {
    const checkAuthState = () => {
      try {
        // Get the current active account inside the effect
        const activeAccount = msalInstance.getActiveAccount();

        // Check if we have an active account
        if (activeAccount) {
          setAuthState({
            isLoading: false,
            hasError: false,
            activeAccount
          });
          return;
        }

        // Check if authentication interaction is in progress
        const interactionInProgress = localStorage.getItem('msal.interaction.status') === 'interaction_in_progress';

        if (interactionInProgress) {
          logger.info('Home', 'Authentication interaction in progress, waiting for completion');
          setAuthState({
            isLoading: true,
            hasError: false
          });
          return;
        }

        // No active account and no interaction in progress
        setAuthState({
          isLoading: false,
          hasError: true,
          errorMessage: 'Authentication required'
        });
      } catch (error) {
        logger.error('Home', 'Error checking authentication state', { error });
        setAuthState({
          isLoading: false,
          hasError: true,
          errorMessage: 'Authentication check failed'
        });
      }
    };

    // Initial check
    checkAuthState();

    // Set up periodic check for authentication state changes
    const interval = setInterval(checkAuthState, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []); // Empty dependency array since we're checking the account inside the effect

  const handleRetryAuth = async () => {
    try {
      setAuthState({
        isLoading: true,
        hasError: false
      });

      await login();
    } catch (error) {
      logger.error('Home', 'Failed to retry authentication', { error });
      setAuthState({
        isLoading: false,
        hasError: true,
        errorMessage: 'Authentication retry failed'
      });
    }
  };

  // Loading state
  if (authState.isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p>Authenticating...</p>
      </div>
    );
  }

  // Error state
  if (authState.hasError) {
    return (
      <div className={styles.errorContainer}>
        <Typography variant="h2">Authentication Required</Typography>
        <p>{authState.errorMessage ?? 'Please sign in to continue'}</p>
        <button className={styles.retryButton} onClick={handleRetryAuth} type="button">
          Sign In
        </button>
      </div>
    );
  }

  // Authenticated state
  if (!authState.activeAccount) {
    return null;
  }

  return <Dashboard />;
}
